import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { useAuthStore } from './auth/store.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { AbsenceScreen } from './screens/AbsenceScreen.js';
import { DashboardScreen } from './screens/DashboardScreen.js';
import { AbsencesScreen } from './screens/admin/AbsencesScreen.js';
import { AdminHomeScreen } from './screens/admin/AdminHomeScreen.js';
import { AdminReportsScreen } from './screens/admin/AdminReportsScreen.js';
import { AttendanceScreen } from './screens/admin/AttendanceScreen.js';
import { EmployeesScreen } from './screens/admin/EmployeesScreen.js';
import { InterviewSlotsScreen } from './screens/admin/InterviewSlotsScreen.js';
import { NormsScreen } from './screens/manager/NormsScreen.js';
import { AcceptanceScreen } from './screens/master/AcceptanceScreen.js';
import { ReceiptsScreen } from './screens/warehouse/ReceiptsScreen.js';
import { ShipmentsScreen } from './screens/warehouse/ShipmentsScreen.js';
import { RecipesScreen } from './screens/manager/RecipesScreen.js';
import { StockScreen } from './screens/manager/StockScreen.js';
import { MissingShiftsScreen } from './screens/admin/MissingShiftsScreen.js';
import { MyReportsScreen } from './screens/MyReportsScreen.js';
import { MyScheduleScreen } from './screens/MyScheduleScreen.js';
import { NotificationsScreen } from './screens/NotificationsScreen.js';
import { ScheduleScreen } from './screens/ScheduleScreen.js';
import { WishesScreen } from './screens/WishesScreen.js';
import { QueueScreen } from './screens/QueueScreen.js';
import { ReportStartScreen } from './screens/ReportStartScreen.js';
import { ReportWizardScreen } from './screens/ReportWizardScreen.js';
import { startSyncEngine, stopSyncEngine } from './sync/index.js';

export function App(): JSX.Element {
  const status = useAuthStore((state) => state.status);
  const restore = useAuthStore((state) => state.restore);

  useEffect(() => {
    void restore();
  }, [restore]);

  // Найм открыт в соседней вкладке и живёт на той же сессии. Если человек
  // вышел там, здесь он выйти тоже должен: иначе он «вышел», но продолжает
  // видеть свой кабинет, пока не наткнётся на первый отказ сервера.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== 'pms.session' || event.newValue !== null) return;
      if (useAuthStore.getState().status !== 'anonymous') void useAuthStore.getState().logout();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Обмен с сервером идёт только под учётной записью: без токена
  // отправлять некуда, а очередь пусть спокойно копится
  useEffect(() => {
    if (status === 'authorized' || status === 'offline-authorized') {
      void startSyncEngine();
      return () => stopSyncEngine();
    }
    return undefined;
  }, [status]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-lg text-dim">
        Загружаю…
      </div>
    );
  }

  if (status === 'anonymous' || status === 'token-expired') {
    return <LoginScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/queue" element={<QueueScreen />} />
        <Route path="/report" element={<ReportStartScreen />} />
        <Route path="/report/:id" element={<ReportWizardScreen />} />
        <Route path="/my-reports" element={<MyReportsScreen />} />
        <Route path="/wishes" element={<WishesScreen />} />
        <Route path="/absence" element={<AbsenceScreen />} />
        <Route path="/my-schedule" element={<MyScheduleScreen />} />
        <Route path="/notifications" element={<NotificationsScreen />} />
        <Route path="/schedule" element={<ScheduleScreen />} />
        <Route path="/dashboard" element={<DashboardScreen />} />
        <Route path="/admin" element={<AdminHomeScreen />} />
        <Route path="/admin/absences" element={<AbsencesScreen />} />
        <Route path="/admin/missing" element={<MissingShiftsScreen />} />
        <Route path="/admin/reports" element={<AdminReportsScreen />} />
        <Route path="/admin/attendance" element={<AttendanceScreen />} />
        <Route path="/admin/employees" element={<EmployeesScreen />} />
        <Route path="/admin/interview-slots" element={<InterviewSlotsScreen />} />
        <Route path="/stock" element={<StockScreen />} />
        <Route path="/recipes" element={<RecipesScreen />} />
        <Route path="/norms" element={<NormsScreen />} />
        <Route path="/master/acceptance" element={<AcceptanceScreen />} />
        <Route path="/receipts" element={<ReceiptsScreen />} />
        <Route path="/shipments" element={<ShipmentsScreen />} />
      </Routes>
    </BrowserRouter>
  );
}
